import { formatDateMMDDyyyy } from '../../../util/DateUtils';
import type { ResolvedPeriod } from '../periodParser';

/**
 * Converts a resolved period into the date parameters the API expects.
 *
 * `ResolvedPeriod` holds ISO days because that is what sorts and compares
 * correctly, but every report endpoint in this codebase parses **MM/DD/YYYY**
 * and rejects ISO outright. Passing `period.startDate` straight into a request
 * is therefore always a bug, and it is an invisible one under test, because
 * mocked request modules accept any string happily.
 *
 * This is deliberately the *only* place that conversion happens. It previously
 * lived inside `countReportRows`, which meant hand-written skills calling a
 * request module directly bypassed it and sent ISO to the server. One boundary
 * is the point: a second one is what caused the bug.
 */
export interface ApiDateRange {
  startDate: string;
  endDate: string;
}

export const apiDateRange = (period: Pick<ResolvedPeriod, 'startDate' | 'endDate'>): ApiDateRange => ({
  startDate: formatDateMMDDyyyy(period.startDate),
  endDate: formatDateMMDDyyyy(period.endDate),
});

/**
 * The same window for request modules that format the dates *themselves*.
 *
 * A few request helpers — the space-usage ones, for instance — take a
 * `MomentInput` and call `moment(date).format('MM/DD/YYYY')` internally. Handing
 * those the output of {@link apiDateRange} would re-parse an already-formatted
 * "08/05/2026", which only survives via moment's deprecated fallback to
 * `new Date()`. ISO is what those helpers actually want.
 *
 * This lives here rather than at the call site so that reading a period's raw
 * dates stays confined to this module — the property the sibling test enforces.
 * Which of the two a skill needs depends on whether its request module formats:
 * if it appends the value straight onto a query string, use `apiDateRange`.
 */
export const isoDateRange = (
  period: Pick<ResolvedPeriod, 'startDate' | 'endDate'>,
): ApiDateRange => ({
  startDate: period.startDate,
  endDate: period.endDate,
});
